import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import algoService from "@/services/algoService";
import type { Algo, AlgoCreate, AlgoTag } from "@/types";
import { extractErrorMessage } from "@/utils/errors";

const RISK_LEVELS = ["LOW", "MEDIUM", "HIGH"];
const SUBSCRIPTION_TYPES = ["MONTHLY", "QUARTERLY", "YEARLY", "LIFETIME"];
const STATUSES = ["ACTIVE", "INACTIVE", "COMING_SOON"];
const ALL_TAGS: AlgoTag[] = ["HIGH_ALPHA", "LOW_RISK", "LOW_CAPITAL"];
const TAG_LABELS: Record<AlgoTag, string> = {
  HIGH_ALPHA: "High Alpha",
  LOW_RISK: "Low Risk",
  LOW_CAPITAL: "Low Capital",
};
// F&O segment/product type — mirrors backend `app.models.algo.AlgoSegment`.
// Admin-facing reference only; NOT read by the Algo Engine at runtime (see
// that class's own docstring) — every real strategy today trades index
// options, hence the default below.
const PRODUCT_TYPES = ["INDEX_OPTIONS", "INDEX_FUTURES", "STOCK_OPTIONS", "STOCK_FUTURES"];
const PRODUCT_TYPE_LABELS: Record<string, string> = {
  INDEX_OPTIONS: "Index Options",
  INDEX_FUTURES: "Index Futures",
  STOCK_OPTIONS: "Stock Options",
  STOCK_FUTURES: "Stock Futures",
};

const EMPTY_FORM: AlgoCreate = {
  algo_name: "",
  description: "",
  risk_level: "MEDIUM",
  capital_required: "0",
  subscription_price: "0",
  subscription_type: "MONTHLY",
  status: "ACTIVE",
  win_ratio: "0",
  drawdown: "0",
  monthly_return: "0",
  total_return: "0",
  image: null,
  display_order: 0,
  tags: [],
  pnl_since_date: new Date().toISOString().slice(0, 10),
  pnl_amount: "0",
  cta_button_text: "",
  is_active: true,
  // F&O Execution — new fields (2026-09-12 admin-form refactor). CAGR/
  // XIRR/Calmar Ratio/Avg Monthly Payout are deliberately NOT included
  // here (or read back in `algoToFormValues` below) — omitting them from
  // the payload entirely leaves them at their existing value (`AlgoUpdate`)
  // or unset/`null` (`AlgoCreate`), matching "no longer required".
  product_type: "INDEX_OPTIONS",
  underlying_symbol: "",
  // `lots_per_trade` deliberately defaults to `null`, NOT `1` (fixed
  // 2026-09-12, post-audit): a Chief-System-Architect audit of this field
  // found that defaulting it to `1` here — combined with `handleUpdate`
  // sending the whole form object on every Save — silently forced
  // `lots_per_trade` to exactly `1` in the database for ANY algo whose
  // value was genuinely unset (`NULL`, meaning "fall back to the algo's
  // legacy `AlgoConfig` lot count"), even when the admin only edited an
  // unrelated field. `null` here round-trips correctly: the backend's
  // `AlgoUpdate` schema treats an explicit `null` exactly like "omitted"
  // (`exclude_none=True` in `AlgoService.update()`), so leaving this
  // field untouched now genuinely leaves the DB value untouched, matching
  // the `cagr`/`xirr`/etc. omission discipline above.
  lots_per_trade: null,
};

function algoToFormValues(algo: Algo): AlgoCreate {
  return {
    algo_name: algo.algo_name,
    description: algo.description,
    risk_level: algo.risk_level,
    capital_required: algo.capital_required,
    subscription_price: algo.subscription_price,
    subscription_type: algo.subscription_type,
    status: algo.status,
    win_ratio: algo.win_ratio,
    drawdown: algo.drawdown,
    monthly_return: algo.monthly_return,
    total_return: algo.total_return,
    image: algo.image ?? "",
    display_order: algo.display_order,
    tags: algo.tags,
    pnl_since_date: algo.pnl_since_date,
    pnl_amount: algo.pnl_amount,
    cta_button_text: algo.cta_button_text ?? "",
    is_active: algo.is_active,
    product_type: algo.product_type ?? "INDEX_OPTIONS",
    underlying_symbol: algo.underlying_symbol ?? "",
    // See `EMPTY_FORM`'s comment above — must stay `?? null`, never `?? 1`.
    lots_per_trade: algo.lots_per_trade ?? null,
  };
}

/** A small bold section header + top divider — used to break the modal
 * into the 5 compact sections below (2026-09-12 admin-form refactor). */
function SectionHeader({ title, first = false }: { title: string; first?: boolean }) {
  return (
    <Grid item xs={12} sx={{ mt: first ? 0 : 1 }}>
      {!first && <Divider sx={{ mb: 1.5 }} />}
      <Typography variant="subtitle2" fontWeight={700} color="text.secondary">
        {title}
      </Typography>
    </Grid>
  );
}

/** Shared create/edit form body — used inside both dialogs below.
 * Refactored (2026-09-12) into 5 compact sections: Basic Details, F&O
 * Execution, Subscription, Backtest Performance, Customer CTA. */
function StrategyFormFields({
  form,
  onChange,
}: {
  form: AlgoCreate;
  onChange: (patch: Partial<AlgoCreate>) => void;
}) {
  const toggleTag = (tag: AlgoTag) => {
    const has = form.tags.includes(tag);
    onChange({ tags: has ? form.tags.filter((t) => t !== tag) : [...form.tags, tag] });
  };

  return (
    <Grid container spacing={2} sx={{ mt: 0.5 }}>
      {/* ---- 1. Basic Details ---- */}
      <SectionHeader title="Basic Details" first />
      <Grid item xs={12} sm={8}>
        <TextField
          label="Strategy name"
          fullWidth
          value={form.algo_name}
          onChange={(e) => onChange({ algo_name: e.target.value })}
          autoFocus
        />
      </Grid>
      <Grid item xs={12} sm={4}>
        <TextField
          label="Display order"
          type="number"
          fullWidth
          value={form.display_order}
          onChange={(e) => onChange({ display_order: Number(e.target.value) })}
        />
      </Grid>
      <Grid item xs={12}>
        <TextField
          label="Description"
          fullWidth
          multiline
          minRows={2}
          value={form.description}
          onChange={(e) => onChange({ description: e.target.value })}
        />
      </Grid>
      <Grid item xs={12} sm={4}>
        <FormControl fullWidth>
          <InputLabel>Risk level</InputLabel>
          <Select
            label="Risk level"
            value={form.risk_level}
            onChange={(e) => onChange({ risk_level: e.target.value })}
          >
            {RISK_LEVELS.map((level) => (
              <MenuItem key={level} value={level}>
                {level}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12} sm={4}>
        <FormControl fullWidth>
          <InputLabel>Status</InputLabel>
          <Select label="Status" value={form.status} onChange={(e) => onChange({ status: e.target.value })}>
            {STATUSES.map((status) => (
              <MenuItem key={status} value={status}>
                {status}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12} sm={4}>
        <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
          Tags
        </Typography>
        <FormGroup row>
          {ALL_TAGS.map((tag) => (
            <FormControlLabel
              key={tag}
              control={<Checkbox size="small" checked={form.tags.includes(tag)} onChange={() => toggleTag(tag)} />}
              label={TAG_LABELS[tag]}
            />
          ))}
        </FormGroup>
      </Grid>

      {/* ---- 2. F&O Execution ---- */}
      <SectionHeader title="F&O Execution" />
      <Grid item xs={12} sm={4}>
        <FormControl fullWidth>
          <InputLabel>Segment / Product type</InputLabel>
          <Select
            label="Segment / Product type"
            value={form.product_type ?? "INDEX_OPTIONS"}
            onChange={(e) => onChange({ product_type: e.target.value })}
          >
            {PRODUCT_TYPES.map((type) => (
              <MenuItem key={type} value={type}>
                {PRODUCT_TYPE_LABELS[type]}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12} sm={4}>
        <TextField
          label="Underlying / Instrument"
          fullWidth
          placeholder="e.g. NIFTY, BANKNIFTY"
          value={form.underlying_symbol ?? ""}
          onChange={(e) => onChange({ underlying_symbol: e.target.value.toUpperCase() })}
          helperText="Reference only — optional."
        />
      </Grid>
      <Grid item xs={12} sm={4}>
        <TextField
          label="Lots per trade"
          type="number"
          fullWidth
          value={form.lots_per_trade ?? ""}
          onChange={(e) => {
            const raw = e.target.value;
            // Empty input -> `null` ("not set"), never `0` — `Number("")`
            // is `0`, which the backend schema rejects (must be > 0) and
            // which is not what a cleared field means anyway.
            onChange({ lots_per_trade: raw === "" ? null : Number(raw) });
          }}
          helperText={
            form.lots_per_trade == null
              ? "Not set — falls back to this algo's legacy config lot count. Order qty = exchange lot size × lots per trade."
              : "Order qty = exchange lot size × lots per trade."
          }
          inputProps={{ min: 1 }}
        />
      </Grid>
      <Grid item xs={12}>
        <TextField
          label="Capital required (₹)"
          fullWidth
          value={form.capital_required}
          onChange={(e) => onChange({ capital_required: e.target.value })}
        />
      </Grid>

      {/* ---- 3. Subscription ---- */}
      <SectionHeader title="Subscription" />
      <Grid item xs={12} sm={6}>
        <FormControl fullWidth>
          <InputLabel>Subscription type</InputLabel>
          <Select
            label="Subscription type"
            value={form.subscription_type}
            onChange={(e) => onChange({ subscription_type: e.target.value })}
          >
            {SUBSCRIPTION_TYPES.map((type) => (
              <MenuItem key={type} value={type}>
                {type}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          label="Subscription price (₹)"
          fullWidth
          value={form.subscription_price}
          onChange={(e) => onChange({ subscription_price: e.target.value })}
        />
      </Grid>

      {/* ---- 4. Backtest Performance ---- */}
      <SectionHeader title="Backtest Performance" />
      <Grid item xs={12}>
        <Typography variant="caption" color="text.secondary">
          Past backtest performance is historical/simulated and is not indicative of future results.
        </Typography>
      </Grid>
      <Grid item xs={12} sm={4}>
        <TextField
          label="Win rate (%)"
          fullWidth
          value={form.win_ratio}
          onChange={(e) => onChange({ win_ratio: e.target.value })}
        />
      </Grid>
      <Grid item xs={12} sm={4}>
        <TextField
          label="Total return (%)"
          fullWidth
          value={form.total_return}
          onChange={(e) => onChange({ total_return: e.target.value })}
        />
      </Grid>
      <Grid item xs={12} sm={4}>
        <TextField
          label="Max drawdown (%)"
          fullWidth
          value={form.drawdown}
          onChange={(e) => onChange({ drawdown: e.target.value })}
        />
      </Grid>
      <Grid item xs={12} sm={4}>
        <TextField
          label="Avg. monthly return (%)"
          fullWidth
          value={form.monthly_return}
          onChange={(e) => onChange({ monthly_return: e.target.value })}
        />
      </Grid>
      <Grid item xs={12} sm={4}>
        <TextField
          label="Total P&L (₹)"
          fullWidth
          value={form.pnl_amount}
          onChange={(e) => onChange({ pnl_amount: e.target.value })}
        />
      </Grid>
      <Grid item xs={12} sm={4}>
        <TextField
          label="Backtest period (since)"
          type="date"
          fullWidth
          InputLabelProps={{ shrink: true }}
          value={form.pnl_since_date}
          onChange={(e) => onChange({ pnl_since_date: e.target.value })}
        />
      </Grid>

      {/* ---- 5. Customer CTA ---- */}
      <SectionHeader title="Customer CTA" />
      <Grid item xs={12} sm={8}>
        <TextField
          label="CTA button text (e.g. Launch Strategy)"
          fullWidth
          value={form.cta_button_text ?? ""}
          onChange={(e) => onChange({ cta_button_text: e.target.value })}
          helperText="Leave blank to show no button on the card."
        />
      </Grid>
      <Grid item xs={12} sm={4} display="flex" alignItems="center">
        <FormControlLabel
          control={
            <Switch
              checked={form.is_active}
              onChange={(e) => onChange({ is_active: e.target.checked })}
            />
          }
          label="Active (visible on public catalogue)"
        />
      </Grid>
    </Grid>
  );
}

export function StrategyManagementPage() {
  const [strategies, setStrategies] = useState<Algo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState<AlgoCreate>(EMPTY_FORM);

  const [editTarget, setEditTarget] = useState<Algo | null>(null);
  const [editForm, setEditForm] = useState<AlgoCreate>(EMPTY_FORM);

  const [deleteTarget, setDeleteTarget] = useState<Algo | null>(null);
  const [saving, setSaving] = useState(false);

  const loadStrategies = useCallback(async () => {
    try {
      const data = await algoService.admin.listAll();
      setStrategies([...data.items].sort((a, b) => a.display_order - b.display_order));
    } catch (err) {
      setError(extractErrorMessage(err));
    }
  }, []);

  useEffect(() => {
    setLoading(true);
    loadStrategies().finally(() => setLoading(false));
  }, [loadStrategies]);

  const handleCreate = async () => {
    setSaving(true);
    try {
      await algoService.admin.create(createForm);
      setCreateOpen(false);
      setCreateForm(EMPTY_FORM);
      await loadStrategies();
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const handleOpenEdit = (algo: Algo) => {
    setEditTarget(algo);
    setEditForm(algoToFormValues(algo));
  };

  const handleUpdate = async () => {
    if (!editTarget) return;
    setSaving(true);
    try {
      await algoService.admin.update(editTarget.id, editForm);
      setEditTarget(null);
      await loadStrategies();
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (algo: Algo) => {
    try {
      await algoService.admin.update(algo.id, { is_active: !algo.is_active });
      await loadStrategies();
    } catch (err) {
      setError(extractErrorMessage(err));
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setSaving(true);
    try {
      await algoService.admin.remove(deleteTarget.id);
      setDeleteTarget(null);
      await loadStrategies();
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const handleMove = async (index: number, direction: -1 | 1) => {
    const target = index + direction;
    if (target < 0 || target >= strategies.length) return;
    const reordered = [...strategies];
    [reordered[index], reordered[target]] = [reordered[target], reordered[index]];
    setStrategies(reordered);
    try {
      await algoService.admin.reorder(reordered.map((s) => s.id));
      await loadStrategies();
    } catch (err) {
      setError(extractErrorMessage(err));
      await loadStrategies();
    }
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Box>
          <Typography variant="h4" fontWeight={700}>
            Strategy Management
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Add, edit, enable/disable, reorder, and rename the Algo Strategies shown on the public
            catalogue — no code changes required.
          </Typography>
        </Box>
        <Button
          startIcon={<AddIcon />}
          variant="contained"
          onClick={() => {
            setCreateForm(EMPTY_FORM);
            setCreateOpen(true);
          }}
        >
          New Strategy
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Order</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Risk</TableCell>
              <TableCell>Tags</TableCell>
              <TableCell align="right">Capital Req.</TableCell>
              <TableCell align="right">XIRR</TableCell>
              <TableCell align="right">Calmar</TableCell>
              <TableCell>CTA Button</TableCell>
              <TableCell>Active</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {!loading && strategies.length === 0 && (
              <TableRow>
                <TableCell colSpan={11} align="center">
                  <Typography variant="body2" color="text.secondary" py={2}>
                    No strategies yet. Click "New Strategy" to add one.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {strategies.map((algo, index) => (
              <TableRow key={algo.id} hover>
                <TableCell>
                  <Box display="flex" alignItems="center">
                    <IconButton
                      size="small"
                      disabled={index === 0}
                      onClick={() => handleMove(index, -1)}
                    >
                      <ArrowUpwardIcon fontSize="inherit" />
                    </IconButton>
                    <IconButton
                      size="small"
                      disabled={index === strategies.length - 1}
                      onClick={() => handleMove(index, 1)}
                    >
                      <ArrowDownwardIcon fontSize="inherit" />
                    </IconButton>
                  </Box>
                </TableCell>
                <TableCell>{algo.algo_name}</TableCell>
                <TableCell>{algo.status}</TableCell>
                <TableCell>{algo.risk_level}</TableCell>
                <TableCell>
                  {algo.tags.map((tag) => (
                    <Chip key={tag} label={TAG_LABELS[tag] ?? tag} size="small" sx={{ mr: 0.5 }} />
                  ))}
                </TableCell>
                <TableCell align="right">₹{algo.capital_required}</TableCell>
                <TableCell align="right">{algo.xirr != null ? `${algo.xirr}%` : "—"}</TableCell>
                <TableCell align="right">{algo.calmar_ratio ?? "—"}</TableCell>
                <TableCell>{algo.cta_button_text || "—"}</TableCell>
                <TableCell>
                  <Switch
                    size="small"
                    checked={algo.is_active}
                    onChange={() => handleToggleActive(algo)}
                  />
                </TableCell>
                <TableCell align="right">
                  <IconButton onClick={() => handleOpenEdit(algo)}>
                    <EditIcon />
                  </IconButton>
                  <IconButton color="error" onClick={() => setDeleteTarget(algo)}>
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Create new strategy</DialogTitle>
        <DialogContent>
          <StrategyFormFields form={createForm} onChange={(patch) => setCreateForm({ ...createForm, ...patch })} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleCreate}
            disabled={saving || !createForm.algo_name || !createForm.description}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(editTarget)} onClose={() => setEditTarget(null)} maxWidth="md" fullWidth>
        <DialogTitle>Edit strategy {editTarget?.algo_name}</DialogTitle>
        <DialogContent>
          <StrategyFormFields form={editForm} onChange={(patch) => setEditForm({ ...editForm, ...patch })} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditTarget(null)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleUpdate}
            disabled={saving || !editForm.algo_name || !editForm.description}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(deleteTarget)} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>Delete strategy {deleteTarget?.algo_name}?</DialogTitle>
        <DialogContent>This cannot be undone.</DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDelete} disabled={saving}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default StrategyManagementPage;
